import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  uniq,
  isPlainObject,
  isNumber,
} from 'lodash-es'
import CardValidator from 'card-validator'
import AcceptedCardTypes from '../maps/acceptedCreditCardTypes'

dayjs.extend(utc)

const CardMasks = {
  default: 'XXXX XXXX XXXX XXXX',
  visa1: 'XXXX XXXX XXXX XXXX',
  visa2: 'XXXX XXXX XXXX XXXXXX',
  visa3: 'XXXX XXXX XXXX XXXXXXX',
  mastercard: 'XXXX XXXX XXXX XXXX',
  reimastercard: 'XXXX XXXX XXXX XXXX',
  discover1: 'XXXX XXXX XXXX XXXX',
  discover2: 'XXXX XXXX XXXX XXXXXXX',
  amex: 'XXXX XXXXXX XXXXX',
  diners: 'XXXX XXXXXX XXXX',
  jcb: 'XXXX XXXX XXXX XXXX',
  unionpay1: 'XXXX XXXX XXXX XXXX',
  unionpay2: 'XXXX XXXX XXXX XXXXX',
  unionpay3: 'XXXX XXXX XXXX XXXXXX',
  unionpay4: 'XXXX XXXX XXXX XXXXXXX',
  maestro1: 'XXXX XXXX XXXX XXXX',
  maestro2: 'XXXX XXXX XXXX XXXXXXX',
}

const CardTypes = {
  visa: 'visa',
  mastercard: 'mastercard',
  reimastercard: 'rei-mastercard',
  discover: 'discover',
  amex: 'american-express',
  diners: 'diners-club',
  jcb: 'jcb',
  unionpay: 'unionpay',
  maestro: 'maestro',
  unknown: '',
}

class CreditCardUtils {
  constructor() {
    // Grab the default values for Union Pay
    const defaultUnionPay = CardValidator.creditCardType.getTypeInfo(CardValidator.creditCardType.types.UNIONPAY)

    // Update the Union Pay card to allow for the new bin range
    // BIN ranges for Union Pay addition 81000000 - 81719999
    CardValidator.creditCardType.updateCard(CardValidator.creditCardType.types.UNIONPAY, {
      lengths: uniq(defaultUnionPay.lengths, [16, 19]),
      patterns: uniq(defaultUnionPay.patterns, [81000000, 81719999]),
    })

    // Add special subset of mastercard for the REI mastercard
    // so that we can display the appropriate image to users
    // BIN ranges for the REI MasterCard 539277 - 539237
    CardValidator.creditCardType.addCard({
      niceType: 'REIMasterCard',
      type: 'rei-mastercard',
      patterns: [539277, 539237, 510415, 511566],
      gaps: [4, 8, 12],
      lengths: [16],
      code: {
        name: 'CVV',
        size: 3,
      },
    })

    // Bind class instance methods to the class
    this.isCardExpired = this.isCardExpired.bind(this)
    this.doesCardExpireBeforeDate = this.doesCardExpireBeforeDate.bind(this)
    // this.formatString = this.formatString.bind(this);
    this.isValidCvv = this.isValidCvv.bind(this)
    this.isValidCvvType = this.isValidCvvType.bind(this)
    this.isValidType = this.isValidType.bind(this)

    // allow external access to CardValidator.creditCardType library
    this.cardValidator = CardValidator
  }

  static get CardMasks() {
    return CardMasks
  }

  isCardExpired(card) {
    if (!card) throw new Error('method `isCardExpired` argument required')
    if (!isPlainObject(card)) throw new Error('method `isCardExpired` requires argument of type object')
    if (!card.expiration) throw new Error('method `isCardExpired` requires `expiration` property on card object')

    const exp = dayjs().year(card.expiration.substr(2)).month(card.expiration.substr(0, 2))
    const now = dayjs().set('date', 1).set({ hour: 0, minute: 0, second: 0 })
    return exp.isBefore(now)
  }

  /**
   * Checks if a credit card expires before a specific date
   * @param {number} timestamp the unix timestamp of the date to compare to the cc expiration
   * @returns {boolean} true if the given timestamp is after the card expires and false otherwise
   */
  doesCardExpireBeforeDate(card, timestamp) {
    if (!card) throw new Error('method `doesCardExpireBeforeDate` requires the `card` argument to be provided')
    if (!timestamp) throw new Error('method `doesCardExpireBeforeDate` requires the `timestamp` argument to be provided')
    if (!isNumber(timestamp)) throw new Error('method `doesCardExpireBeforeDate` requires `timestamp` argument to be of type number')
    if (!card.expiration) throw new Error('method `doesCardExpireBeforeDate` requires `expiration` property on card object')

    const exp = dayjs.utc().year(card.expiration.substr(2)).month(card.expiration.substr(0, 2) - 1).endOf('month')
    return exp.isBefore(dayjs.utc(timestamp))
  }

  static getMask(type, rawLength) {
    let mask = ''

    switch (type) {
    case CardTypes.visa:
      if (rawLength <= 16) mask = CardMasks.visa1
      else if (rawLength <= 18) mask = CardMasks.visa2
      else if (rawLength > 18) mask = CardMasks.visa3
      break
    case CardTypes.mastercard:
      mask = CardMasks.mastercard
      break
    case CardTypes.reimastercard:
      mask = CardMasks.reimastercard
      break
    case CardTypes.jcb:
      mask = CardMasks.jcb
      break
    case CardTypes.unionpay:
      if (rawLength <= 16) mask = CardMasks.unionpay1
      else if (rawLength === 17) mask = CardMasks.unionpay2
      else if (rawLength === 18) mask = CardMasks.unionpay3
      else if (rawLength > 18) mask = CardMasks.unionpay4
      break
    case CardTypes.maestro:
      if (rawLength <= 16) mask = CardMasks.maestro1
      else if (rawLength > 16) mask = CardMasks.maestro2
      break
    case CardTypes.discover:
      if (rawLength <= 16) mask = CardMasks.discover1
      else if (rawLength > 16) mask = CardMasks.discover2
      break
    case CardTypes.amex:
      mask = CardMasks.amex
      break
    case CardTypes.diners:
      mask = CardMasks.diners
      break
    default:
      mask = CardMasks.default
      break
    }

    return mask
  }

  isValidCvv(number, cvv) {
    const card = CardValidator.creditCardType(number)
    if (!card.length) return true // unknown card type, so optimism
    if (card.length > 1) return true // list needs to be narrowed down
    return /\d/.test(cvv) && cvv.length === card[0].code.size
  }

  isValidCvvType(type, cvv) {
    const cards = CardValidator.creditCardType('')
    const filteredCard = cards.filter(c => c.type === type.toLowerCase())
    if (filteredCard.length !== 1) return true // no matching type
    return /\d/.test(cvv) && cvv.length === filteredCard[0].code.size
  }

  isValidType(cardType) {
    return AcceptedCardTypes.some(element => (element === cardType))
  }

  isCompleteCard(card) {
    if (card && card.number && card.expiration && card.pin) return true
    return false
  }
}

export default CreditCardUtils
